import React, { useMemo } from "react";
import { PopoverPosition } from "@blueprintjs/core";
import classNames from "classnames";
import { FieldArray } from "react-final-form-arrays";
import { get } from "lodash";
import { useSelector } from "react-redux";
import SourceRow from "../SourceRow";
import Button from "components/Button";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { SpaceviewerElementFormField } from "pages/RS-Workspace/components/Results/components/ElementDialog";
import { defaultSource } from "../..";
import SourceRowPreview from "../SourceRowPreview";
import { HelpId, HELP_SYSTEM_URL } from "shared/helpSystemBindings";
import { processVariablesSelector } from "store/global/global.selectors";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { isSpaceviewerSourceCompleted } from "store/rs-workspace/utils/completeness.utils";
import { mapSpaceViewerDataToSourceTemplate } from "store/rs-workspace/utils/results.utils";
import PlusIcon from "icons/plus.svg";
import * as styles from "../../styles.module.scss";
const MAX_SOURCES = 2;
const Sources = ({ elementData, editMode, restrictions, resultSetId, upsert, remove, handleChangeView, }) => {
    const processVariables = useSelector(processVariablesSelector);
    const sources = useMemo(() => get(elementData, SpaceviewerElementFormField.sources), [elementData]);
    const addSourceButtonDisabled = useMemo(() => sources.some((source) => !isSpaceviewerSourceCompleted(mapSpaceViewerDataToSourceTemplate(source), processVariables)) || sources?.length >= MAX_SOURCES, [processVariables, sources]);
    const addButtonTitle = useMemo(() => {
        if (!addSourceButtonDisabled) {
            return "Add source";
        }
        if (sources?.length >= MAX_SOURCES) {
            return `Maximum number of sources is ${MAX_SOURCES}.`;
        }
        return "Complete existing source.";
    }, [addSourceButtonDisabled, sources?.length]);
    const sourcesRestrictions = useMemo(() => getFieldRestrictions(restrictions, [getRequestName(SpaceviewerElementFormField.sources)])
        ?.SpaceViewElementSource, [restrictions]);
    return (React.createElement("div", { className: styles.sources },
        React.createElement("div", { className: styles.header },
            React.createElement("div", { className: classNames(styles.cell, styles.view) }, "View"),
            React.createElement("div", { className: classNames(styles.cell, styles.source) },
                "Data source",
                sourcesRestrictions?.Source?.IsMandatory ? " *" : ""),
            React.createElement("div", { className: classNames(styles.cell, styles.variable) },
                "Variable",
                sourcesRestrictions?.Source?.Simulation?.VariableType?.IsMandatory ? " *" : ""),
            React.createElement("div", { className: classNames(styles.cell, styles.unit) },
                "Units",
                sourcesRestrictions?.Source?.Simulation?.Units?.IsMandatory ? " *" : ""),
            React.createElement("div", { className: classNames(styles.cell, styles.timing) }, "Timing"),
            editMode ? (React.createElement(Button, { label: React.createElement(PlusIcon, { width: 25, height: 25 }), square: true, position: PopoverPosition.RIGHT, onClick: () => upsert(defaultSource), showTooltipIfDisabled: true, title: addButtonTitle, classes: {
                    root: classNames(styles.addSource, {
                        [styles.disabled]: addSourceButtonDisabled,
                    }),
                }, disabled: addSourceButtonDisabled })) : (React.createElement("div", { className: classNames(styles.cell, styles.delete) }))),
        React.createElement("div", { className: styles.body }, editMode ? (React.createElement("div", null,
            React.createElement(FieldArray, { name: `${SpaceviewerElementFormField.sources}` }, ({ fields }) => fields.map((name, index) => (React.createElement(SourceRow, { key: name, name: name, source: fields.value[index], sources: sources, upsert: upsert, remove: remove, restrictions: sourcesRestrictions })))),
            React.createElement(LearnMoreLink, { className: styles.linkBlock, url: `${HELP_SYSTEM_URL}#cshid=${HelpId.SPACE_VIEWER}` }))) : (sources.map((source, index) => (React.createElement(SourceRowPreview, { key: index, source: source, parameters: elementData.parameters, resultSetId: resultSetId, elementId: elementData.id, handleChangeView: () => handleChangeView(source.id) })))))));
};
export default Sources;
